import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  ClipboardCheck,
  CalendarIcon,
  Clock,
  MapPin,
  ArrowUpRight,
  Loader2,
  CheckCircle2,
} from "lucide-react";

import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { useT } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const clipboardImg =
  "https://static.prod-images.emergentagent.com/jobs/2b9abe43-28cb-4bd1-9e3c-60fa851731cb/images/840d7d2e6d570b8ee3e2e16af9d774af750d8b9b7db0b8cc86071537adf320b0.png";

const SUPPORTED_CITIES = [
  "Naples", "Bonita Springs", "Estero", "Marco Island",
  "Fort Myers", "Cape Coral", "Lehigh Acres", "North Fort Myers",
  "San Carlos Park", "Punta Gorda", "Port Charlotte", "North Port",
  "Sarasota", "Immokalee",
];

const services = [
  { value: "Four Point Inspection", label: "Four Point Inspection", durationLabel: "≈ 45 min" },
  { value: "Wind Mitigation", label: "Wind Mitigation", durationLabel: "≈ 45 min" },
  { value: "Full Home Inspection", label: "Full Home Inspection", durationLabel: "≈ 2.5 hrs" },
  { value: "Roof Inspection", label: "Roof Inspection", durationLabel: "≈ 45 min" },
];

const purposes = [
  { value: "Pre-Purchase", labelKey: "purpose.pre_purchase" },
  { value: "New Build", labelKey: "purpose.new_build" },
  { value: "Pre-Sale", labelKey: "purpose.pre_sale" },
  { value: "Insurance", labelKey: "purpose.insurance" },
];

const timePrefs = [
  { id: "any", labelKey: "appt.slot.any" },
  { id: "morning", labelKey: "appt.slot.morning" },
  { id: "afternoon", labelKey: "appt.slot.afternoon" },
];

const inputClass =
  "w-full bg-slate-50 border border-slate-300 rounded-none focus-visible:border-[#1F3460] focus-visible:ring-1 focus-visible:ring-[#1F3460] focus-visible:ring-offset-0 h-12 px-4 text-[#1F3460] placeholder:text-slate-400";

const fmt12 = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${m.toString().padStart(2, "0")} ${ampm}`;
};

export default function Appointment() {
  const { t } = useT();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [date, setDate] = useState(null);
  const [openDate, setOpenDate] = useState(false);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [timePref, setTimePref] = useState("any"); // any | morning | afternoon
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    service_type: "",
    purpose: "",
    preferred_time: "",
    notes: "",
  });

  const isOtherCity = form.city === "__OTHER__";
  const dateStr = useMemo(() => (date ? format(date, "yyyy-MM-dd") : ""), [date]);

  // Fetch available slots when city + date + service are all set (and city is supported)
  useEffect(() => {
    setSlots([]);
    setSlotsError("");
    if (!form.city || isOtherCity || !form.service_type || !dateStr) return;
    let cancelled = false;
    (async () => {
      setSlotsLoading(true);
      try {
        const { data } = await axios.get(`${API}/availability`, {
          params: { date: dateStr, city: form.city, service: form.service_type },
        });
        if (cancelled) return;
        setSlots(data.slots || []);
        if ((!data.slots || data.slots.length === 0) && data.reason) {
          setSlotsError(data.reason);
        }
      } catch (err) {
        if (!cancelled) {
          setSlotsError(err?.response?.data?.detail || "Could not load times.");
        }
      } finally {
        if (!cancelled) setSlotsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.city, form.service_type, dateStr, isOtherCity]);

  // When city or date or service changes, clear any previously selected time
  useEffect(() => {
    setForm((f) => ({ ...f, preferred_time: "" }));
  }, [form.city, form.service_type, dateStr]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.address || !form.service_type || !date) {
      toast.error(t("toast.fill_required"));
      return;
    }
    // If a supported city is chosen, require a selected time slot
    if (form.city && !isOtherCity && !form.preferred_time) {
      toast.error(t("toast.pick_slot"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        city: isOtherCity ? "" : form.city,
        preferred_date: format(date, "yyyy-MM-dd"),
      };
      await axios.post(`${API}/appointments`, payload);
      toast.success(t("toast.received"));
      setDone(true);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || t("toast.send_fail");
      toast.error(typeof detail === "string" ? detail : t("toast.send_fail"));
    } finally {
      setSubmitting(false);
    }
  };

  const morningSlots = slots.filter((s) => parseInt(s.split(":")[0], 10) < 12);
  const afternoonSlots = slots.filter((s) => parseInt(s.split(":")[0], 10) >= 12);
  const showMorning = timePref !== "afternoon";
  const showAfternoon = timePref !== "morning";

  return (
    <div data-testid="appointment-page" className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-72px)]">
      {/* LEFT panel */}
      <aside
        className="relative hidden lg:block lg:sticky lg:top-[72px] lg:h-[calc(100vh-72px)] overflow-hidden"
        style={{
          backgroundImage: `url(${clipboardImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-[#1F3460]/75" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1F3460]/95 via-[#1F3460]/60 to-transparent" />
        <div className="relative z-10 h-full p-10 xl:p-16 flex flex-col justify-between text-white">
          <div>
            <ClipboardCheck className="w-10 h-10 text-[#6FA8DC]" />
            <div className="overline text-[#6FA8DC] mt-8">{t("appt.aside.overline")}</div>
            <h1 className="font-display text-5xl xl:text-6xl font-black mt-4 leading-[0.95]">
              {t("appt.aside.title")}
            </h1>
            <p className="mt-6 text-slate-200 max-w-md text-lg">
              {t("appt.aside.body_pre")}<strong>{t("appt.aside.body_strong")}</strong>{t("appt.aside.body_post")}
            </p>
            <div className="mt-5 p-4 bg-[#6FA8DC]/15 border-l-4 border-[#6FA8DC] text-sm text-slate-100">
              <strong>{t("appt.aside.disclaimer_bold")}</strong> {t("appt.aside.disclaimer")}
            </div>
          </div>

          <ul className="space-y-4 text-slate-200">
            {[
              "appt.aside.b1",
              "appt.aside.b2",
              "appt.aside.b3",
            ].map((k, i) => (
              <li key={i} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#6FA8DC]" />
                <span>{t(k)}</span>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      {/* RIGHT panel — form */}
      <section className="px-5 md:px-10 lg:px-14 py-16 md:py-20">
        <div className="max-w-xl">
          {done ? (
            <div data-testid="appointment-success" className="border-2 border-[#1F3460] p-10">
              <CheckCircle2 className="w-12 h-12 text-[#6FA8DC]" />
              <h2 className="font-display text-3xl md:text-4xl font-black text-[#1F3460] mt-4">
                {t("appt.success.title")}
              </h2>
              <p className="mt-3 text-[#5E6B85]">
                {t("appt.success.body_pre")}{form.name || "—"}{t("appt.success.body_post")}{form.email || "your email"}{t("appt.success.body_post2")}
              </p>
              <button
                onClick={() => {
                  setDone(false);
                  setForm({
                    name: "",
                    email: "",
                    phone: "",
                    address: "",
                    city: "",
                    service_type: "",
                    purpose: "",
                    preferred_time: "",
                    notes: "",
                  });
                  setDate(null);
                }}
                data-testid="appointment-submit-another"
                className="btn-outline mt-8"
              >
                {t("appt.success.another")} <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <div className="overline">{t("appt.step")}</div>
              <h1 className="font-display text-4xl md:text-5xl font-black text-[#1F3460] mt-3 leading-[1.05] lg:hidden">
                {t("appt.heading_mobile")}
              </h1>
              <p className="mt-4 text-[#5E6B85] lg:hidden">
                {t("appt.subhead_mobile")}
              </p>
              <div className="mt-4 p-4 bg-[#6FA8DC]/10 border-l-4 border-[#6FA8DC] text-sm text-[#1F3460] lg:hidden">
                <strong>{t("appt.disc_mobile_bold")}</strong> {t("appt.disc_mobile")}
              </div>

              <form
                onSubmit={onSubmit}
                data-testid="appointment-form"
                className="mt-8 space-y-5"
                noValidate
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">{t("appt.field.name")}</Label>
                    <Input
                      id="name"
                      data-testid="appointment-name"
                      className={inputClass}
                      placeholder="Jane Doe"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">{t("appt.field.phone")}</Label>
                    <Input
                      id="phone"
                      data-testid="appointment-phone"
                      className={inputClass}
                      placeholder="(555) 123-4567"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">{t("appt.field.email")}</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="appointment-email"
                    className={inputClass}
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="address" className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">{t("appt.field.address")}</Label>
                  <Input
                    id="address"
                    data-testid="appointment-address"
                    className={inputClass}
                    placeholder="123 Palm Ave, Naples, FL 34102"
                    value={form.address}
                    onChange={(e) => update("address", e.target.value)}
                    required
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">{t("appt.field.city")}</Label>
                  <Select value={form.city} onValueChange={(v) => update("city", v)}>
                    <SelectTrigger
                      data-testid="appointment-city-trigger"
                      className="h-12 rounded-none bg-slate-50 border border-slate-300 focus:border-[#1F3460] focus:ring-1 focus:ring-[#1F3460] text-[#1F3460]"
                    >
                      <SelectValue placeholder={t("appt.field.city_ph")} />
                    </SelectTrigger>
                    <SelectContent className="rounded-none max-h-72">
                      {SUPPORTED_CITIES.map((c) => (
                        <SelectItem
                          key={c}
                          value={c}
                          data-testid={`appointment-city-${c.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          {c}
                        </SelectItem>
                      ))}
                      <SelectItem value="__OTHER__" data-testid="appointment-city-other">
                        {t("appt.field.city_other")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {isOtherCity && (
                    <p className="mt-2 text-xs text-[#5E6B85]">
                      {t("appt.city_other_help")}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">
                      {t("appt.field.service")}
                    </Label>
                    <Select
                      value={form.service_type}
                      onValueChange={(v) => update("service_type", v)}
                    >
                      <SelectTrigger
                        data-testid="appointment-service-trigger"
                        className="h-12 rounded-none bg-slate-50 border border-slate-300 focus:border-[#1F3460] focus:ring-1 focus:ring-[#1F3460] text-[#1F3460]"
                      >
                        <SelectValue placeholder={t("appt.field.service_ph")} />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {services.map((s) => (
                          <SelectItem
                            key={s.value}
                            value={s.value}
                            data-testid={`appointment-service-${s.value.replace(/\s+/g, "-").toLowerCase()}`}
                          >
                            <span className="flex items-center justify-between gap-3 w-full">
                              <span>{s.label}</span>
                              <span className="text-[10px] tracking-widest uppercase text-[#5E6B85]">
                                {s.durationLabel}
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">{t("appt.field.purpose")}</Label>
                    <Select value={form.purpose} onValueChange={(v) => update("purpose", v)}>
                      <SelectTrigger
                        data-testid="appointment-purpose-trigger"
                        className="h-12 rounded-none bg-slate-50 border border-slate-300 focus:border-[#1F3460] focus:ring-1 focus:ring-[#1F3460] text-[#1F3460]"
                      >
                        <SelectValue placeholder={t("appt.field.purpose_ph")} />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {purposes.map((p) => (
                          <SelectItem
                            key={p.value}
                            value={p.value}
                            data-testid={`appointment-purpose-${p.value.replace(/\s+/g, "-").toLowerCase()}`}
                          >
                            {t(p.labelKey)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">{t("appt.field.date")}</Label>
                  <Popover open={openDate} onOpenChange={setOpenDate}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="appointment-date-trigger"
                        className={`${inputClass} flex items-center justify-between text-left`}
                      >
                        <span className={date ? "text-[#1F3460]" : "text-slate-400"}>
                          {date ? format(date, "PPP") : t("appt.field.date_ph")}
                        </span>
                        <CalendarIcon className="w-4 h-4 text-[#1F3460]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0 rounded-none border-2 border-[#1F3460] bg-white"
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(d) => {
                          setDate(d);
                          setOpenDate(false);
                        }}
                        disabled={(d) => {
                          const today = new Date(new Date().setHours(0, 0, 0, 0));
                          const max = new Date(today);
                          max.setDate(max.getDate() + 60);
                          return d < today || d > max || d.getDay() === 0; // Sundays off
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Time slot picker — only when a supported city + date + service are picked */}
                {form.city && !isOtherCity && form.service_type && date && (
                  <div data-testid="appointment-timeslots" className="border-l-4 border-[#6FA8DC] pl-5 py-2">
                    <Label className="text-xs font-bold uppercase tracking-widest text-[#1F3460] flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5" /> {t("appt.slot.title")}
                    </Label>
                    <p className="mt-1 text-xs text-[#5E6B85]">
                      {t("appt.slot.subtitle_pre")}{form.city}{t("appt.slot.subtitle_on")}{date ? format(date, "EEE, MMM d") : ""}.
                    </p>

                    {slotsLoading && (
                      <div className="mt-4 flex items-center gap-2 text-[#5E6B85] text-sm">
                        <Loader2 className="w-4 h-4 animate-spin" /> {t("appt.slot.loading")}
                      </div>
                    )}

                    {!slotsLoading && slots.length === 0 && (
                      <div className="mt-4 bg-slate-50 border border-slate-200 p-4 text-sm text-[#1F3460]">
                        <MapPin className="w-4 h-4 inline mr-1.5 text-[#6FA8DC]" />
                        {slotsError || t("appt.slot.none")}
                      </div>
                    )}

                    {!slotsLoading && slots.length > 0 && (
                      <div className="mt-4 space-y-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[11px] font-bold uppercase tracking-widest text-[#5E6B85] mr-2">{t("appt.slot.timepref")}</span>
                          {timePrefs.map((p) => (
                            <button
                              type="button"
                              key={p.id}
                              data-testid={`time-pref-${p.id}`}
                              onClick={() => setTimePref(p.id)}
                              className={`px-3 py-1.5 text-xs font-bold tracking-wider uppercase border-2 transition-all ${
                                timePref === p.id
                                  ? "bg-[#1F3460] text-white border-[#1F3460]"
                                  : "bg-white text-[#5E6B85] border-slate-300 hover:border-[#1F3460]"
                              }`}
                            >
                              {t(p.labelKey)}
                            </button>
                          ))}
                        </div>

                        {showMorning && morningSlots.length > 0 && (
                          <div>
                            <div className="overline">{t("appt.slot.morning")}</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {morningSlots.map((s) => (
                                <button
                                  type="button"
                                  key={s}
                                  data-testid={`time-slot-${s}`}
                                  onClick={() => update("preferred_time", s)}
                                  className={`px-3.5 py-2 text-sm font-bold tracking-wide border-2 transition-all ${
                                    form.preferred_time === s
                                      ? "bg-[#1F3460] text-white border-[#1F3460]"
                                      : "bg-white text-[#1F3460] border-slate-300 hover:border-[#1F3460]"
                                  }`}
                                >
                                  {fmt12(s)}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {showAfternoon && afternoonSlots.length > 0 && (
                          <div>
                            <div className="overline">{t("appt.slot.afternoon")}</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {afternoonSlots.map((s) => (
                                <button
                                  type="button"
                                  key={s}
                                  data-testid={`time-slot-${s}`}
                                  onClick={() => update("preferred_time", s)}
                                  className={`px-3.5 py-2 text-sm font-bold tracking-wide border-2 transition-all ${
                                    form.preferred_time === s
                                      ? "bg-[#1F3460] text-white border-[#1F3460]"
                                      : "bg-white text-[#1F3460] border-slate-300 hover:border-[#1F3460]"
                                  }`}
                                >
                                  {fmt12(s)}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {((timePref === "morning" && morningSlots.length === 0) ||
                          (timePref === "afternoon" && afternoonSlots.length === 0)) && (
                          <p className="text-sm text-[#5E6B85]">
                            {t("appt.slot.none_pref", { pref: t(timePref === "morning" ? "appt.slot.morning" : "appt.slot.afternoon") })}
                          </p>
                        )}
                        {form.preferred_time && (
                          <p className="text-xs text-[#1F3460]">
                            <strong>{t("appt.slot.selected")}</strong> {fmt12(form.preferred_time)} on{" "}
                            {date ? format(date, "EEEE, MMMM d") : ""}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <Label htmlFor="notes" className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">{t("appt.field.notes")}</Label>
                  <Textarea
                    id="notes"
                    data-testid="appointment-notes"
                    rows={4}
                    placeholder={t("appt.field.notes_ph")}
                    className="w-full bg-slate-50 border border-slate-300 rounded-none focus-visible:border-[#1F3460] focus-visible:ring-1 focus-visible:ring-[#1F3460] focus-visible:ring-offset-0 px-4 py-3 text-[#1F3460] placeholder:text-slate-400"
                    value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                  />
                </div>

                <button
                  type="submit"
                  data-testid="appointment-submit"
                  disabled={submitting}
                  className="btn-accent w-full mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> {t("appt.submitting")}
                    </>
                  ) : (
                    <>
                      {t("appt.submit")} <ArrowUpRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                <p className="text-xs text-[#5E6B85] pt-2">
                  {t("appt.disclaimer_bottom")}
                </p>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
